import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'

import {
  SanityBlogPostPage,
  SanityBlogPostWithoutBody,
} from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'

import {
  getAllBlogSettings,
  getBlogLocale,
  getBlogPostPage,
  getBlogPosts,
} from '@lib/sanity/blog'
import { getPreview } from '@lib/sanity/client'
import { getAllSlugs } from '@lib/sanity/page'
import { useBlogCanonicalUrl } from '@lib/metadata'
import { getAllLocales, Locale } from '@lib/language'
import {
  CustomStaticPropsContext,
  PageType,
  StaticPathWithSlug,
} from '@lib/routes'

import Modules from '@modules/modules'
import Layout from '@components/layout'
import { getBlogPostSchema } from '@lib/schema'

interface BlogPostPageProps {
  locale: Locale
  page: SanityBlogPostPage
  site: SanitySiteFragment
}

const BlogPostPage = ({ page, site }: BlogPostPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl(
    PageType.BLOG_POST,
    page.slug,
    site.blogSettings
  )

  const blogPostSchema = getBlogPostSchema(page, site)

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      blogPostSchema={blogPostSchema}
      className="bg-gray text-black"
    >
      <Modules posts={page.blogPosts} />
    </Layout>
  )
}

interface BlogPostPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<BlogPostPageParams>): Promise<
  GetStaticPropsResult<BlogPostPageProps>
> {
  const blogLocale = await getBlogLocale(locale)
  const blogPostPage = await getBlogPostPage(
    locale,
    blogLocale,
    params?.slug ?? '',
    getPreview(preview, previewData)
  )

  if (!blogPostPage.page) {
    return { notFound: true }
  }

  // TODO: Do all this in a single query.
  let blogPosts: Array<SanityBlogPostWithoutBody> = []
  const blogPostLimit = 5

  const relatedBlogPosts = await getBlogPosts(
    locale,
    0,
    blogPostLimit,
    blogPostPage.page.categories?.map((category) => category.slug) ?? [],
    [blogPostPage.page.id]
  )

  if (relatedBlogPosts) {
    blogPosts = relatedBlogPosts
  }

  if (blogPosts.length < blogPostLimit) {
    const additionalBlogPosts = await getBlogPosts(
      locale,
      0,
      blogPostLimit - blogPosts.length,
      [],
      [blogPostPage.page.id, ...(relatedBlogPosts?.map(({ id }) => id) ?? [])]
    )

    if (additionalBlogPosts) {
      blogPosts = [...blogPosts, ...additionalBlogPosts]
    }
  }

  return {
    props: {
      locale,
      page: { ...blogPostPage.page, blogPosts },
      site: blogPostPage.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogPostPageParams>
> {
  const blogPosts = await getAllSlugs('blogPost')
  const siteLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(siteLocales)
  const paths: StaticPathWithSlug[] = []

  blogPosts.forEach((blogPost) => {
    const locale = blogPost.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({ params: { slug: blogPost.slug }, locale })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({ params: { slug: blogPost.slug }, locale: childLocale })
      }
    }
  })

  return { paths, fallback: 'blocking' }
}

export default BlogPostPage
